import React, { useEffect, useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../AuthContext";
import { useActiveBar } from "../ActiveBarContext";
import { loadBarWeek } from "../shared/barStats";

// App-only offers: the customer taps "Use offer" in the app, shows the
// live screen to the bartender, and taps Redeem. Each redemption is
// counted, so the owner sees how many people actually came in.
// Offers are stored on the bar doc as `offers`.

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const LIMITS = [
  { value: "day", label: "Once per day" },
  { value: "week", label: "Once per week" },
  { value: "once", label: "One time only" },
];

function shortTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const p = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m ? `${h12}:${String(m).padStart(2, "0")} ${p}` : `${h12} ${p}`;
}

function whenLabel(o) {
  const days = !o.days || o.days.length === 0 || o.days.length === 7 ? "Every day" : o.days.join(", ");
  const hours = o.startTime && o.endTime ? ` · ${shortTime(o.startTime)}–${shortTime(o.endTime)}` : " · All day";
  return days + hours;
}

const card = { background: "#17131F", borderRadius: "14px", padding: "18px" };
const input = {
  width: "100%", boxSizing: "border-box", minHeight: "44px", padding: "10px 12px", borderRadius: "10px",
  border: "1px solid #3A3350", background: "#0D0B1A", color: "#fff", fontSize: "16px",
};
const label = { display: "block", fontWeight: 600, margin: "0 0 6px" };

export default function Offers() {
  const { refreshUserBars } = useAuth();
  const { myBars, activeBar } = useActiveBar();
  const [offers, setOffers] = useState([]);
  const [usedThisWeek, setUsedThisWeek] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [days, setDays] = useState([]);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [limit, setLimit] = useState("day");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    setOffers(activeBar?.offers || []);
    setUsedThisWeek(null);
    if (activeBar) loadBarWeek(activeBar.id).then((t) => setUsedThisWeek(t.offersUsed)).catch(() => {});
    clearForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBar?.id]);

  if (myBars.length === 0) {
    return <p style={{ color: "#fff", fontSize: "16px" }}>Claim your bar first, then you can create offers here.</p>;
  }

  function clearForm() {
    setEditingId(null);
    setTitle("");
    setDetails("");
    setDays([]);
    setStartTime("");
    setEndTime("");
    setLimit("day");
  }

  const toggleDay = (d) => setDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));

  const saveOffers = async (next) => {
    await updateDoc(doc(db, "bars", activeBar.id), { offers: next });
    setOffers(next);
    await refreshUserBars();
  };

  const submit = async (e) => {
    e.preventDefault();
    setMessage("");
    if (!title.trim()) return setMessage("Give the offer a short name, like \"$1 off your first tap\".");
    if ((startTime && !endTime) || (!startTime && endTime)) return setMessage("Set both a start and end time, or leave both empty for all day.");
    setSaving(true);
    const fields = { title: title.trim(), details: details.trim(), days, startTime, endTime, limit, active: true };
    const next = editingId
      ? offers.map((o) => (o.id === editingId ? { ...o, ...fields } : o))
      : [...offers, { id: `offer-${Date.now()}`, ...fields }];
    try {
      await saveOffers(next);
      setMessage(editingId ? "Saved." : "Offer is live in the app.");
      clearForm();
    } catch (err) {
      setMessage(`Couldn't save: ${err.message}`);
    }
    setSaving(false);
  };

  const startEdit = (o) => {
    setEditingId(o.id);
    setTitle(o.title || "");
    setDetails(o.details || "");
    setDays(o.days || []);
    setStartTime(o.startTime || "");
    setEndTime(o.endTime || "");
    setLimit(o.limit || "day");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleActive = (o) => saveOffers(offers.map((x) => (x.id === o.id ? { ...x, active: x.active === false } : x)));

  const remove = async (o) => {
    if (!window.confirm(`Delete "${o.title}"?`)) return;
    await saveOffers(offers.filter((x) => x.id !== o.id));
    if (editingId === o.id) clearForm();
  };

  return (
    <div style={{ color: "#fff", maxWidth: "820px", display: "flex", flexDirection: "column", gap: "20px" }}>
      <div>
        <h1 style={{ margin: 0, fontFamily: "'Bebas Neue', Inter, sans-serif", fontWeight: 400, fontSize: "44px", letterSpacing: "0.5px" }}>
          App-only offers
        </h1>
        <p style={{ margin: "4px 0 0", fontSize: "16px", color: "#ACA8BF", lineHeight: 1.5 }}>
          Customers tap "Use offer" in the app and show the live screen to your bartender, then tap Redeem.
          Every use is counted, so you can see exactly how many people Bar Fun Finder brought in.
        </p>
      </div>

      <div style={{ ...card, display: "flex", gap: "12px", alignItems: "baseline" }}>
        <span style={{ fontSize: "34px", fontWeight: 700 }}>{usedThisWeek ?? "–"}</span>
        <span style={{ color: "#ACA8BF", fontSize: "16px" }}>offers used at {activeBar?.name || "your bar"} in the last 7 days</span>
      </div>

      <form onSubmit={submit} style={{ ...card, display: "flex", flexDirection: "column", gap: "14px" }}>
        <h2 style={{ margin: 0, fontSize: "20px" }}>{editingId ? "Edit offer" : "New offer"}</h2>
        <div>
          <label htmlFor="offer-title" style={label}>Offer</label>
          <input id="offer-title" style={input} value={title} maxLength={60} onChange={(e) => setTitle(e.target.value)} placeholder="$1 off your first tap" />
        </div>
        <div>
          <label htmlFor="offer-details" style={label}>Fine print (optional)</label>
          <input id="offer-details" style={input} value={details} maxLength={140} onChange={(e) => setDetails(e.target.value)} placeholder="Domestic taps only. Dine-in." />
        </div>
        <div>
          <span style={label}>Days (leave all off for every day)</span>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {DAYS.map((d) => {
              const on = days.includes(d);
              return (
                <button
                  key={d}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleDay(d)}
                  style={{
                    minWidth: "52px", minHeight: "44px", borderRadius: "22px", cursor: "pointer",
                    border: on ? "none" : "1px solid #3A3350", background: on ? "#FF3D81" : "transparent",
                    color: on ? "#0B0B12" : "#fff", font: `${on ? 700 : 500} 15px Inter, sans-serif`,
                  }}
                >
                  {d}
                </button>
              );
            })}
          </div>
        </div>
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 160px" }}>
            <label htmlFor="offer-start" style={label}>Starts (optional)</label>
            <input id="offer-start" type="time" style={input} value={startTime} onChange={(e) => setStartTime(e.target.value)} />
          </div>
          <div style={{ flex: "1 1 160px" }}>
            <label htmlFor="offer-end" style={label}>Ends (optional)</label>
            <input id="offer-end" type="time" style={input} value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          </div>
          <div style={{ flex: "1 1 200px" }}>
            <label htmlFor="offer-limit" style={label}>Each customer can use it</label>
            <select id="offer-limit" style={input} value={limit} onChange={(e) => setLimit(e.target.value)}>
              {LIMITS.map((l) => (
                <option key={l.value} value={l.value}>{l.label}</option>
              ))}
            </select>
          </div>
        </div>
        {message && <p style={{ margin: 0, color: message.startsWith("Couldn't") ? "#FF6B8A" : "#4ADE80" }}>{message}</p>}
        <div style={{ display: "flex", gap: "10px" }}>
          <button type="submit" disabled={saving} style={{ minHeight: "48px", padding: "0 22px", border: "none", borderRadius: "12px", background: "#FF3D81", color: "#0B0B12", font: "700 16px Inter, sans-serif", cursor: "pointer", opacity: saving ? 0.6 : 1 }}>
            {saving ? "Saving…" : editingId ? "Save changes" : "Publish offer"}
          </button>
          {editingId && (
            <button type="button" onClick={clearForm} style={{ minHeight: "48px", padding: "0 18px", borderRadius: "12px", border: "1px solid #3A3350", background: "transparent", color: "#fff", fontSize: "16px", cursor: "pointer" }}>
              Cancel
            </button>
          )}
        </div>
      </form>

      <section style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <h2 style={{ margin: 0, fontSize: "20px" }}>Your offers</h2>
        {offers.length === 0 && <p style={{ margin: 0, color: "#ACA8BF" }}>No offers yet.</p>}
        {offers.map((o) => (
          <div key={o.id} style={{ ...card, display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap", opacity: o.active === false ? 0.6 : 1 }}>
            <div style={{ flex: "1 1 260px", minWidth: 0 }}>
              <div style={{ fontSize: "17px", fontWeight: 700 }}>
                {o.title} {o.active === false && <span style={{ color: "#ACA8BF", fontWeight: 500 }}>(paused)</span>}
              </div>
              {o.details && <div style={{ color: "#D9CCFF", fontSize: "15px" }}>{o.details}</div>}
              <div style={{ color: "#ACA8BF", fontSize: "14px" }}>
                {whenLabel(o)} · {(LIMITS.find((l) => l.value === o.limit) || LIMITS[0]).label}
              </div>
            </div>
            <div style={{ display: "flex", gap: "4px" }}>
              <button type="button" onClick={() => startEdit(o)} style={{ minHeight: "44px", padding: "0 10px", background: "transparent", border: "none", color: "#B9A6FF", fontSize: "16px", cursor: "pointer" }}>Edit</button>
              <button type="button" onClick={() => toggleActive(o)} style={{ minHeight: "44px", padding: "0 10px", background: "transparent", border: "none", color: "#fff", fontSize: "16px", cursor: "pointer" }}>{o.active === false ? "Resume" : "Pause"}</button>
              <button type="button" onClick={() => remove(o)} style={{ minHeight: "44px", padding: "0 10px", background: "transparent", border: "none", color: "#F472B6", fontSize: "16px", cursor: "pointer" }}>Delete</button>
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
